/**
 * CartStyles.js
 * Handles all inline styling for cart notepad elements
 */

export class CartStyles {
  /**
   * Apply all styles to notepad element
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleNotepad(notepad) {
    this.styleNotepadContainer(notepad);
    this.styleHeader(notepad);
    this.styleCloseButton(notepad);
    this.styleContent(notepad);
    this.styleScrollAreas(notepad);
    this.styleItems(notepad);
    this.styleFooter(notepad);
    this.styleTotalSection(notepad);
    this.styleActionButtons(notepad);
    this.injectAnimationStyles();
  }

  /**
   * Style the main notepad container
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleNotepadContainer(notepad) {
    const cartButton = document.querySelector('.cart-btn, #sidebar-cart-btn');
    let cartRect = { top: 20, right: 20, width: 50 };

    if (cartButton) {
      cartRect = cartButton.getBoundingClientRect();
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    Object.assign(notepad.style, {
      position: 'absolute',
      top: `${cartRect.bottom + scrollTop + 8}px`,
      right: '16px',
      width: '360px',
      maxWidth: '90vw',
      background: 'var(--color-bg-primary, white)',
      border: '1px solid var(--color-border-light, #e5e5e5)',
      borderRadius: '16px',
      boxShadow: '0 16px 48px rgba(0, 0, 0, 0.16), 0 6px 18px rgba(0, 0, 0, 0.08)',
      zIndex: '1003',
      transform: 'translateY(-100%)',
      opacity: '0',
      transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
      fontFamily: 'var(--font-family-primary, system-ui)',
      backdropFilter: 'blur(12px)',
      maxHeight: '72vh',
      overflowY: 'auto'
    });
  }

  /**
   * Style the notepad header
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleHeader(notepad) {
    const header = notepad.querySelector('.notepad-header');
    if (header) {
      Object.assign(header.style, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: header.querySelector('.notepad-title') ? 'flex-start' : 'flex-end',
        padding: header.querySelector('.notepad-title') ? '16px 24px' : '0',
        borderBottom: 'none'
      });
    }
  }

  /**
   * Style the close button
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleCloseButton(notepad) {
    const closeBtn = notepad.querySelector('.notepad-close');
    if (closeBtn) {
      Object.assign(closeBtn.style, {
        width: '32px',
        height: '32px',
        background: 'var(--color-neutral-100, #f5f5f5)',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        color: 'var(--color-text-secondary, #666)',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: '0'
      });
    }
  }

  /**
   * Style the content section
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleContent(notepad) {
    const content = notepad.querySelector('.notepad-content');
    if (content) {
      Object.assign(content.style, { padding: '20px 24px' });
    }
  }

  /**
   * Style scrollable areas
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleScrollAreas(notepad) {
    const scrollAreas = notepad.querySelectorAll('.notepad-lines');
    scrollAreas.forEach(area => {
      Object.assign(area.style, {
        maxHeight: 'calc(60vh)',
        overflowY: 'auto',
        paddingRight: '8px'
      });
    });
  }

  /**
   * Style individual cart items
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleItems(notepad) {
    const items = notepad.querySelectorAll('.notepad-item');
    items.forEach((item, index) => {
      Object.assign(item.style, {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 16px',
        borderBottom: index < items.length - 1 ? '1px solid var(--color-border-light, #e5e5e5)' : 'none',
        position: 'relative'
      });

      if (item.classList.contains('newly-added')) {
        Object.assign(item.style, {
          backgroundColor: 'var(--color-success-alpha-10, rgba(34, 197, 94, 0.12))',
          borderRadius: '8px',
          boxShadow: 'inset 0 0 0 1px var(--color-success-alpha-30, rgba(34, 197, 94, 0.3))',
          animation: 'highlightNewItem 1.8s ease-out'
        });
      }

      this.styleItemIndicator(item);
      this.styleItemDetails(item);
      this.styleItemPrice(item);
    });
  }

  /**
   * Style item indicator (quantity badge)
   * @param {HTMLElement} item - Item DOM element
   */
  static styleItemIndicator(item) {
    const indicator = item.querySelector('.item-indicator');
    if (indicator) {
      Object.assign(indicator.style, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: '0'
      });
    }

    const quantityBadge = item.querySelector('.quantity-badge');
    if (quantityBadge) {
      Object.assign(quantityBadge.style, {
        width: '28px',
        height: '28px',
        background: 'var(--color-primary, #2563eb)',
        color: 'white',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: '700',
        boxShadow: 'none',
        border: 'none'
      });
    }
  }

  /**
   * Style item details (name, modifiers, portion)
   * @param {HTMLElement} item - Item DOM element
   */
  static styleItemDetails(item) {
    const details = item.querySelector('.item-details');
    if (details) {
      Object.assign(details.style, {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flex: '1',
        minWidth: '0'
      });
    }

    const name = item.querySelector('.item-name');
    if (name) {
      Object.assign(name.style, {
        fontSize: '14px',
        fontWeight: '600',
        color: 'var(--color-text-primary, #333)',
        lineHeight: '1.3'
      });
    }

    const modifiers = item.querySelector('.item-modifiers, .item-portion');
    if (modifiers) {
      Object.assign(modifiers.style, {
        fontSize: '12px',
        fontWeight: '400',
        color: 'var(--color-text-secondary, #666)',
        lineHeight: '1.2'
      });
    }
  }

  /**
   * Style item price
   * @param {HTMLElement} item - Item DOM element
   */
  static styleItemPrice(item) {
    const price = item.querySelector('.item-price');
    if (price) {
      Object.assign(price.style, {
        fontSize: '14px',
        fontWeight: '700',
        color: 'var(--color-primary, #2563eb)',
        textAlign: 'right',
        flexShrink: '0'
      });
    }
  }

  /**
   * Style footer section
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleFooter(notepad) {
    const footer = notepad.querySelector('.notepad-footer');
    if (footer) {
      Object.assign(footer.style, {
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid var(--color-border-light, #e5e5e5)'
      });
    }
  }

  /**
   * Style total section
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleTotalSection(notepad) {
    const totalSection = notepad.querySelector('.total-section');
    if (totalSection) {
      Object.assign(totalSection.style, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        padding: '12px 16px',
        background: 'var(--color-primary-alpha-5, rgba(59, 130, 246, 0.05))',
        borderRadius: '8px',
        border: '1px solid var(--color-primary-alpha-20, rgba(59, 130, 246, 0.2))'
      });
    }

    const totalLabel = notepad.querySelector('.total-label');
    if (totalLabel) {
      Object.assign(totalLabel.style, {
        fontSize: '16px',
        fontWeight: '600',
        color: 'var(--color-text-primary, #333)'
      });
    }

    const totalAmount = notepad.querySelector('.total-amount');
    if (totalAmount) {
      Object.assign(totalAmount.style, {
        fontSize: '18px',
        fontWeight: '800',
        color: 'var(--color-primary, #2563eb)'
      });
    }
  }

  /**
   * Style action buttons
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static styleActionButtons(notepad) {
    const primaryBtns = notepad.querySelectorAll('.view-full-order-btn, .confirm-cart-btn, .pay-all-btn, .confirm-pending-btn');
    primaryBtns.forEach((btn) => {
      Object.assign(btn.style, {
        width: '100%',
        background: 'var(--color-primary, #2563eb)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        padding: '14px 20px',
        fontSize: '15px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        boxShadow: '0 2px 8px var(--color-primary-alpha-30, rgba(59, 130, 246, 0.3))'
      });
    });
  }

  /**
   * Inject CSS animation styles
   */
  static injectAnimationStyles() {
    if (!document.querySelector('#notepad-animations')) {
      const style = document.createElement('style');
      style.id = 'notepad-animations';
      style.textContent = `
        @keyframes highlightNewItem {
          0% { background-color: rgba(34, 197, 94, 0.18); }
          100% { background-color: transparent; }
        }
      `;
      document.head.appendChild(style);
    }
  }
}
